import React from 'react';
import { compact } from 'lodash';
import { shouldShowBumpTag } from '@reverbdotcom/commons/src/components/bump_tag';
import {
  CoreMarketplaceCombinedMarketplaceSearch,
  Ad,
} from '../../schemas/rql';
import { PageSettings } from './map_params';
import { IUser } from '@reverbdotcom/commons/src/components/user_context_provider';
import TileCurationView from '../discovery/curation/tile_curation_view';
import ListingCard from '@reverbdotcom/commons/src/components/listing_card';
import { AdCard } from '@reverbdotcom/commons/src/components/ads/ad_card';
import ListViewRowCard from '../list_view_row_card';
import { WatchParentComponentNames } from '@reverbdotcom/commons/src/event_tracking/watchlist';
import { RCListingGrid } from '@reverbdotcom/cadence/components';

const COMPONENT_NAME = 'InterspersedListings';

interface IListingsProps {
  listing: CoreMarketplaceCombinedMarketplaceSearch.Listings;
  idx: number;
  user: IUser;
  showOnlySold?: boolean;
  trackingName?: string;
  trackingQuery?: string;
  pageSettings?: PageSettings;
  displayAsRows?: boolean;
  loading?: boolean;
  useRCListingGrid?: boolean;
}

function listingKey(listing: CoreMarketplaceCombinedMarketplaceSearch.Listings): string {
  if (listing.bumpKey?.key) {
    return `${listing.id}-${listing.bumpKey?.key}`;
  }

  return listing.id;
}

export function Listing({
  listing,
  idx,
  user,
  showOnlySold = false,
  trackingName = COMPONENT_NAME,
  trackingQuery = '',
  pageSettings = undefined,
  displayAsRows = false,
  loading = false,
  useRCListingGrid = false,
}: IListingsProps) {
  if (displayAsRows) {
    return (
      <ListViewRowCard
        key={listing.id}
        listing={listing}
        loading={loading}
        position={idx}
        trackingQuery={trackingQuery}
      />
    );
  }

  return (
    <ListingCard
      key={listing.id}
      listing={listing}
      position={idx}
      soldListing={showOnlySold}
      trackingName={trackingName}
      trackingQuery={trackingQuery}
      watchBadgeParentComponentName={WatchParentComponentNames.MarketplaceGridWatch}
      useRCListingCard={useRCListingGrid}
      showViewedTag
      footerContent={
        user.isAdmin &&
        <TileCurationView
          listingId={listing.id}
          pageSettings={pageSettings}
          bumped={listing.bumped}
        />
      }
    />
  );
}

// This function intersperses the bump listings amongst normal listings.
// It is setup so that every 2nd and 3rd tile (of out a set of 7) in the grid are bumped listings.
function intersperseBumps(listings, bumps, starting, frequency) {
  let bumpIndex = 0;
  const interspersedArray = [];
  for (let i = 0; i < listings.length; i += 1) {
    if ((i + starting) % frequency === 0) {
      interspersedArray.push(bumps[bumpIndex], bumps[bumpIndex + 1]);
      bumpIndex += 2;
    }
    interspersedArray.push(listings[i]);
  }

  return compact(interspersedArray);
}

function setTrackingName(listing) {
  return shouldShowBumpTag(listing) ? `Bumped${COMPONENT_NAME}` : COMPONENT_NAME;
}

const TILE_CLASSNAME_BASE = 'sortable-tile';

interface AdListingItemProps {
  ad: Ad;
  idx: number;
  useRCListingGrid?: boolean;
}

function AdListingItem( { ad, idx, useRCListingGrid = false }: AdListingItemProps) {
  const adCardElement = (
    <div>
      <AdCard
        ad={ad}
        key={idx}
        componentName="CombinedBumpListingGrid"
      />
    </div>
  );

  if (useRCListingGrid) {
    return (
      <RCListingGrid.Item>
        {adCardElement}
      </RCListingGrid.Item>
    );
  }

  return (
    <li
      className={TILE_CLASSNAME_BASE}
      key={ad.uuid}
    >
      {adCardElement}
    </li>
  );
}

interface ListingItemProps {
  listing: CoreMarketplaceCombinedMarketplaceSearch.Listings;
  idx: number;
  user: IUser
  showOnlySold: any;
  trackingQuery?: string;
  pageSettings?: PageSettings;
  displayAsRows?: boolean;
  loading?: boolean;
  useRCListingGrid?: boolean;
}

function ListingItem({
  listing,
  idx,
  user,
  showOnlySold,
  trackingQuery = '',
  pageSettings = undefined,
  displayAsRows = false,
  loading = false,
  useRCListingGrid = false,
}: ListingItemProps) {

  const listingElement = (
    <Listing
      listing={listing}
      idx={idx}
      user={user}
      showOnlySold={showOnlySold}
      trackingName={setTrackingName(listing)}
      trackingQuery={trackingQuery}
      pageSettings={pageSettings}
      displayAsRows={displayAsRows}
      loading={loading}
      useRCListingGrid={useRCListingGrid}
    />
  );

  if (useRCListingGrid) {
    return (
      <RCListingGrid.Item>
        {listingElement}
      </RCListingGrid.Item>
    );
  }

  return (
    <li
      className={displayAsRows ? '' : TILE_CLASSNAME_BASE}
      key={listingKey(listing)}
    >
      {listingElement}
    </li>
  );
}

function randomAdPosition() {
  // provides a random position in the second row (index positions 5 through 9)
  const AD_POSITION_MIN = 5;
  const AD_POSITION_MAX = 9;
  return Math.floor(Math.random() * (AD_POSITION_MAX - AD_POSITION_MIN + 1) + AD_POSITION_MIN);
}

// used for calculating the idx of listing items if an ad is inserted
function listingItemAdsIndex(
  index: number,
  adIndex: number,
  isAd: boolean,
): number {
  if (!isAd) return index;

  return index < adIndex ? index : index + 1;
}

interface RenderListingsProps {
  listings: CoreMarketplaceCombinedMarketplaceSearch.Listings[];
  ad: Ad;
  showOnlySold: boolean;
  trackingQuery: string;
  user: IUser;
  pageSettings?: PageSettings;
  displayAsRows?: boolean;
  loading?: boolean;
  useRCListingGrid?: boolean;
}

function ListingsWithAd({
  listings,
  ad,
  showOnlySold,
  trackingQuery,
  user,
  pageSettings = undefined,
  displayAsRows = false,
  loading = false,
  useRCListingGrid = false,
}: RenderListingsProps) {
  const adPosition = randomAdPosition();

  const listingItems = listings.map((listing, idx) =>
    <ListingItem
      key={listingKey(listing)}
      listing={listing}
      idx={listingItemAdsIndex(idx, adPosition, !!ad)}
      user={user}
      showOnlySold={showOnlySold}
      trackingQuery={trackingQuery}
      pageSettings={pageSettings}
      displayAsRows={displayAsRows}
      loading={loading}
      useRCListingGrid={useRCListingGrid}
    />,
  );

  if (!ad) {
    return (
      <>{listingItems}</>
    );
  }

  const adItem = (
    <AdListingItem
      key={ad.uuid}
      idx={adPosition}
      ad={ad}
      useRCListingGrid={useRCListingGrid}
    />
  );

  listingItems.splice(adPosition, 0, adItem);

  return (
    <>{listingItems}</>
  );
}

interface IProps {
  user: IUser;
  listings: CoreMarketplaceCombinedMarketplaceSearch.Listings[];
  bumpedListings: CoreMarketplaceCombinedMarketplaceSearch.Listings[];
  showOnlySold?: boolean;
  trackingQuery?: string;
  ad?: Ad;
  pageSettings?: PageSettings;
  displayAsRows?: boolean;
  loading?: boolean;
  useRCListingGrid?: boolean;
}

export default function InterspersedListings({
  user,
  listings,
  bumpedListings,
  showOnlySold = false,
  trackingQuery = '',
  ad = undefined,
  pageSettings = undefined,
  displayAsRows = false,
  loading = false,
  useRCListingGrid = false,
}: IProps) {
  const combinedListings = intersperseBumps(listings, bumpedListings, 4, 5);
  const listingsToUse = listings.length < 25 ? listings : combinedListings;

  return (
    <ListingsWithAd
      listings={listingsToUse}
      ad={ad}
      showOnlySold={showOnlySold}
      trackingQuery={trackingQuery}
      user={user}
      pageSettings={pageSettings}
      displayAsRows={displayAsRows}
      loading={loading}
      useRCListingGrid={useRCListingGrid}
    />
  );
}
